import { inject, Injectable, signal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { CachedProjectService } from "../../../shared/services/cached-project.service";
import { JobDetail } from "../../../models/jobDetail";
import {
  BatchOutput,
  CapturedImage,
  CaptureGroup,
  delay,
  downloadCaptures,
  nextFrame,
} from "../../../shared/batch-capture";
import { stripDash } from "../chart-slice/chart-slice.component";

export interface ShotBatchRequest {
  jobIds: string[];
  shotId: number;
  output: BatchOutput;
}

// the shot/iteration the shots page should show for the job it loads next
export interface ShotBatchTarget {
  shotId: number | null;
  iteration: number;
  x?: number | null; // shot location, only used to restore the user's original selection
  y?: number | null;
}

// The shots page keeps its state on the component, so it exposes just what the
// runner needs through this interface instead of the runner reaching into it
export interface ShotBatchHost {
  currentJobId(): string | null;
  currentIteration(): number;
  currentShot(): { id: number | null; x: number | null; y: number | null };
  // applied whenever the page loads a job, until cleared with null. While set, the page
  // neither restores nor saves per-job settings, so every job is shown with the same view
  setBatchTarget(target: ShotBatchTarget | null): void;
  // re-load the current job with the batch target (switching to the same job emits nothing)
  reloadWithBatchTarget(): void;
  isBusy(): boolean;
  // true once the page has loaded this exact shot for the current job
  showsShot(shotId: number): boolean;
  capture(): Promise<CapturedImage>;
}

// upper bound for a single job to load before we capture anyway,
// so one stuck request can't hang the whole batch
const SETTLE_TIMEOUT_MS = 60_000;
// the page flips its loading flags asynchronously (the image spinner only after 250ms),
// so give it a moment before we start watching for "everything idle"
const SETTLE_GRACE_MS = 500;
// requests chain (shot list -> shot image -> graph redraw), so require a short
// fully-idle window before declaring the page settled. Longer than the 250ms the page
// waits before raising its image spinner, so that idle gap isn't mistaken for "done"
const SETTLE_STABLE_MS = 600;

@Injectable({ providedIn: "root" })
export class ShotBatchScreenshotService {
  private cachedProjectService = inject(CachedProjectService);
  private nzNotificationService = inject(NzNotificationService);

  // --- batch run state (drives the progress UI on the shots page) ---
  readonly running = signal(false);
  readonly done = signal(0);
  readonly total = signal(0);
  readonly currentLabel = signal("");
  private cancelRequested = false;

  cancel() {
    this.cancelRequested = true;
  }

  async runBatch(request: ShotBatchRequest, host: ShotBatchHost) {
    if (this.running()) return;
    const jobIds = [...new Set(request.jobIds)];
    if (jobIds.length === 0 || request.shotId == null) return;

    const originalJobId = host.currentJobId();
    const originalIteration = host.currentIteration();
    const originalShot = host.currentShot();

    this.running.set(true);
    this.cancelRequested = false;
    this.done.set(0);
    this.total.set(jobIds.length);

    const groups: CaptureGroup[] = [];
    const failures: string[] = [];
    let missing = 0;

    try {
      for (const jobId of jobIds) {
        if (this.cancelRequested) break;

        // every job is shown at the iteration the user is on (clamped to what the job has)
        host.setBatchTarget({ shotId: request.shotId, iteration: originalIteration });
        let job: JobDetail;
        try {
          if (host.currentJobId() === jobId) {
            job = await firstValueFrom(this.cachedProjectService._getJobDetail(jobId));
            host.reloadWithBatchTarget();
          } else {
            job = await firstValueFrom(this.cachedProjectService.getJobDetail(jobId));
          }
        } catch (err) {
          console.error("Shot batch screenshot: failed to load job", jobId, err);
          failures.push(jobId);
          this.done.update((d) => d + 1);
          continue;
        }

        this.currentLabel.set(`${job.name} · Shot ${request.shotId}`);
        await this.waitForPageSettled(host);
        if (this.cancelRequested) break;

        // the job doesn't have this shot (or its traces) — just move on to the next job
        if (!host.showsShot(request.shotId)) {
          missing++;
          this.done.update((d) => d + 1);
          continue;
        }

        const label = `${job.name} · CP ${host.currentIteration()} · Shot ${request.shotId}`;
        try {
          groups.push({ title: label, shots: [await host.capture()] });
        } catch (err) {
          console.error("Shot batch screenshot failed for", label, err);
          failures.push(label);
        }
        this.done.update((d) => d + 1);
      }

      const shots = groups.flatMap((g) => g.shots);
      if (shots.length) {
        const projectName = (await this.projectName(originalJobId)) ?? "project";
        const baseName =
          `${stripDash(projectName)}-shot${request.shotId}-screenshots-` +
          new Date().toISOString().slice(0, 10);
        await downloadCaptures(request.output, groups, baseName);
      }

      // jobs without the shot are expected, so they're only counted, never reported as errors
      const outputLabel = request.output === "slides" ? "added to a slide deck" : "downloaded as a zip";
      const skipped = missing ? ` ${missing} job(s) didn't have shot ${request.shotId}.` : "";
      // sticky (nzDuration 0) so the outcome isn't missed after a background-tab run
      if (this.cancelRequested) {
        this.nzNotificationService.info(
          "Batch screenshots cancelled",
          `${shots.length} screenshot(s) were captured before cancelling.`,
          { nzDuration: 0 },
        );
      } else if (failures.length) {
        this.nzNotificationService.warning(
          "Batch screenshots finished with errors",
          `Captured ${shots.length} screenshot(s). Failed: ${failures.join(", ")}.${skipped}`,
          { nzDuration: 0 },
        );
      } else if (shots.length) {
        this.nzNotificationService.success(
          "Batch screenshots complete",
          `${shots.length} screenshot(s) ${outputLabel}.${skipped}`,
          { nzDuration: 0 },
        );
      } else {
        this.nzNotificationService.info(
          "Nothing captured",
          `None of the selected jobs had shot ${request.shotId}.`,
          { nzDuration: 0 },
        );
      }
    } finally {
      // put the page back on the job/iteration/shot the user was looking at
      host.setBatchTarget({
        shotId: originalShot.id,
        iteration: originalIteration,
        x: originalShot.x,
        y: originalShot.y,
      });
      try {
        if (originalJobId && host.currentJobId() !== originalJobId) {
          await firstValueFrom(this.cachedProjectService.getJobDetail(originalJobId));
        } else {
          host.reloadWithBatchTarget();
        }
      } catch (err) {
        console.error("Shot batch screenshot: failed to restore original job", err);
      }
      host.setBatchTarget(null);
      this.currentLabel.set("");
      this.running.set(false);
    }
  }

  private async projectName(jobId: string | null): Promise<string | null> {
    if (!jobId) return null;
    try {
      return (await firstValueFrom(this.cachedProjectService._getJobDetail(jobId))).projectName;
    } catch {
      return null;
    }
  }

  private async waitForPageSettled(host: ShotBatchHost) {
    await delay(SETTLE_GRACE_MS);
    const start = Date.now();
    let idleSince: number | null = null;
    while (Date.now() - start < SETTLE_TIMEOUT_MS) {
      if (this.cancelRequested) return;
      if (!host.isBusy()) {
        idleSince ??= Date.now();
        if (Date.now() - idleSince >= SETTLE_STABLE_MS) break;
      } else {
        idleSince = null;
      }
      await delay(100);
    }
    // two frames so the graphs repaint with the loaded data before capture
    await nextFrame();
    await nextFrame();
  }
}
